import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../shared/abstractStore';
import { profileSupportingDataQuery } from '../generated/profileSupportingDataQuery';
import { mainApiConfig } from '../../../configure/mainApiConfig';
import { subscriptionTeamFieldsFragment } from '../models/SubscriptionTeam';
import { subscriptionFieldsFragment } from '../models/Subscription';
import { Guid } from 'guid-string';

export interface ProfileSupportingDataOptions extends AsyncLoadOptions {
    subscriptionId?: string,
}

/**
 * Get a the supporting data needed to interactivly edit an Profile.
 * @param id
 * @param options
 */
export function useProfileSupportingData(options: ProfileSupportingDataOptions = {}): AsyncLoadResult<profileSupportingDataQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<profileSupportingDataQuery>(
        gql`
        query profileSupportingDataQuery ($subscriptionIdOrEmptyGuid: ID!) {
            roleGroups {
                id
                name
            }

            subscription(id: $subscriptionIdOrEmptyGuid) {
                ...subscriptionFields
            }

            subscriptionTeams (subscriptionId: $subscriptionIdOrEmptyGuid) {
                ...subscriptionTeamFields
            }
        }
        ${subscriptionFieldsFragment}
        ${subscriptionTeamFieldsFragment}
        `,
        {
            variables: {
                subscriptionIdOrEmptyGuid: options.subscriptionId ?? Guid.empty,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
